import { buildListingsQueryParams } from 'src/adapters/storeToPageURL/state/buildListingsQueryParams';
import { facetsToSelections } from 'src/adapters/storeToPageURL/state/buildListingsURLFromState';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { TriggerName } from 'src/redux/products/types';

const sortAndPageFromState = (state: ReduxApplicationState, productsQuery = state.productsQuery) =>
  buildListingsQueryParams({ productsQuery }, { page: productsQuery.page });

const getActiveFacetValuesByAlias = (
  facets: ReduxApplicationState['productsQuery']['facets'],
): Map<string, Set<string>> => {
  return new Map(
    facets.map((facet) => [
      facet.alias,
      new Set(facet.options.filter((option) => option.active).map((option) => option.value)),
    ]),
  );
};

const getFacetToggleCounts = (
  previousFacets: ReduxApplicationState['productsQuery']['facets'],
  currentFacets: ReduxApplicationState['productsQuery']['facets'],
): { activatedCount: number; deactivatedCount: number } => {
  const previousActive = getActiveFacetValuesByAlias(previousFacets);
  const currentActive = getActiveFacetValuesByAlias(currentFacets);

  let activatedCount = 0;
  let deactivatedCount = 0;

  previousActive.forEach((previousValues, alias) => {
    const currentValues = currentActive.get(alias) ?? new Set<string>();

    previousValues.forEach((value) => {
      if (!currentValues.has(value)) {
        deactivatedCount += 1;
      }
    });

    currentValues.forEach((value) => {
      if (!previousValues.has(value)) {
        activatedCount += 1;
      }
    });
  });

  return { activatedCount, deactivatedCount };
};

const hasActiveFacets = (facets: ReduxApplicationState['productsQuery']['facets']): boolean =>
  facets.some((facet) => facet.options.some((option) => option.active));

// Stable, canonical signature of the active selections (taxonomy levels and
// `/-/...` filters alike) for equality comparison between two states. Aliases
// and their values are sorted so the signature is order-independent.
const filterSignature = (productsQuery: ReduxApplicationState['productsQuery']): string =>
  JSON.stringify(
    facetsToSelections(productsQuery.facets)
      .map((selection) => ({ alias: selection.alias, values: [...selection.values].sort() }))
      .sort((a, b) => (a.alias < b.alias ? -1 : a.alias > b.alias ? 1 : 0)),
  );

export const getRouteChangeTriggerName = (
  previousState: ReduxApplicationState,
  currentState: ReduxApplicationState,
): TriggerName => {
  const currentPq = previousState.productsQuery;
  const nextPq = currentState.productsQuery;

  const current = sortAndPageFromState(previousState, currentPq);
  const next = sortAndPageFromState(currentState, nextPq);

  const sortChanged = current.get('sort') !== next.get('sort');
  const pageChanged = current.get('page') !== next.get('page');
  const filterChanged = filterSignature(currentPq) !== filterSignature(nextPq);

  const { activatedCount, deactivatedCount } = getFacetToggleCounts(
    currentPq.facets,
    nextPq.facets,
  );
  const nextHasNoActiveFacets = !hasActiveFacets(nextPq.facets);

  if (pageChanged && !filterChanged && !sortChanged) {
    return TriggerName.PAGE_NUMBER_CHANGE;
  }
  if (sortChanged && !filterChanged) {
    return TriggerName.CHANGE_SORT;
  }

  if (filterChanged && deactivatedCount > 0 && activatedCount === 0) {
    if (deactivatedCount === 1 && !sortChanged) {
      return TriggerName.RESET_FACET;
    }

    if (nextHasNoActiveFacets) {
      return TriggerName.RESET_ALL_FACETS_AND_SORT;
    }
  }

  return TriggerName.CHANGE_FACETS;
};
